import React, { useEffect, useMemo, useState } from "react";
import { listarGastosMes, crearGastoMes, pagosComparativa } from "../api";
import BalanceComparativa from "./BalanceComparativa";
import "./BalanceTab.css";

export default function BalanceTab({
  mes,
  setMes,
  ingresos,
  pagosMes = [],
  sesionesPendientes = [],
}) {
  const [gastos, setGastos] = useState([]);
  const [totalGastos, setTotalGastos] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [nuevoConcepto, setNuevoConcepto] = useState("");
  const [nuevoImporte, setNuevoImporte] = useState("");

  const [subTab, setSubTab] = useState("resumen");

  const [comparativaData, setComparativaData] = useState([]);
  const [comparativaLoading, setComparativaLoading] = useState(false);
  const [comparativaError, setComparativaError] = useState("");

  const mesTexto = useMemo(() => {
    if (!mes) return "";
    const d = new Date(mes + "-01");
    if (Number.isNaN(d.getTime())) return mes;
    return d.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
  }, [mes]);

  const selectedYear = useMemo(() => {
    const y = Number(String(mes || "").slice(0, 4));
    return Number.isFinite(y) && y > 2000 ? y : new Date().getFullYear();
  }, [mes]);

  const normCode = (v) =>
    String(v ?? "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[_\s]+/g, "-");

  const getSpecialtyCode = (item) =>
    item?.specialty_code ||
    item?.specialty ||
    item?.specialty_name ||
    item?.especialidad ||
    "";

  const getBaseSpecialtyCode = (value) => {
    const code = normCode(value);

    if (
      code === "psicologia-g" ||
      code === "psicologia-m" ||
      code === "psicologia-adultos"
    ) {
      return "psicologia";
    }

    if (code === "logopeda") {
      return "logopedia";
    }

    return code;
  };

  const getFactorCentro = (item) => {
    const specialty = getBaseSpecialtyCode(getSpecialtyCode(item));
    const professionalId = Number(item?.professional_id || 0);

    if (specialty === "psicologia") {
      // Teresa (id 2): 100 % para el centro
      if (professionalId === 2) return 1;

      // Marina (id 17): 40 % para el centro
      if (professionalId === 17) return 0.4;

      // Marta (id 18): 30 % para el centro
      if (professionalId === 18) return 0.3;

      return 1;
    }

    // Las dos logopedas: 30 % para el centro
    if (specialty === "logopedia") {
      return 0.3;
    }

    // Nutrición: se mantiene en 30 %
    if (specialty === "nutricion") {
      return 0.3;
    }

    return 1;
  };

  const ingresosRealCentro = useMemo(() => {
    if (!Array.isArray(pagosMes) || pagosMes.length === 0) {
      return Number(ingresos || 0);
    }

    return pagosMes.reduce((acc, p) => {
      const amt = Number(p.amount) || 0;
      const factor = getFactorCentro(p);
      return acc + amt * factor;
    }, 0);
  }, [pagosMes, ingresos]);

  const ingresosNum = useMemo(
    () => Number(ingresosRealCentro || 0),
    [ingresosRealCentro]
  );

  const balance = useMemo(
    () => ingresosNum - Number(totalGastos || 0),
    [ingresosNum, totalGastos]
  );

  const cargarGastos = async (month) => {
    if (!month) return;

    try {
      setLoading(true);
      setError("");
      const data = await listarGastosMes(month);
      setGastos(data.items || []);
      setTotalGastos(data.total || 0);
    } catch (e) {
      console.error(e);
      setError(e.message || "Error al cargar gastos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mes) cargarGastos(mes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes]);

  const handleAddGasto = async () => {
    const imp = parseFloat(String(nuevoImporte).replace(",", "."));

    if (!nuevoConcepto.trim()) {
      alert("Introduce un concepto de gasto (ej. Alquiler, Luz...)");
      return;
    }

    if (Number.isNaN(imp) || imp <= 0) {
      alert("Introduce un importe válido mayor que 0");
      return;
    }

    try {
      await crearGastoMes({
        mes,
        concepto: nuevoConcepto.trim(),
        importe: imp,
      });

      setNuevoConcepto("");
      setNuevoImporte("");
      await cargarGastos(mes);
    } catch (e) {
      alert(e.message || "Error al guardar gasto");
    }
  };

  /*
   * Cambio de tarifas:
   * - Antes del 01/09/2026: tarifas antiguas.
   * - Desde el 01/09/2026: tarifas actuales de AgendarSesion.
   *
   * Si una sesión ya tiene sessions.precio, ese valor tiene prioridad
   * y no se recalcula con ninguna tabla.
   */
  const FECHA_CAMBIO_TARIFAS = "2026-09-01";

  const PRECIOS_ANTIGUOS_POR_ESPECIALIDAD = useMemo(
    () => ({
      psicologia: {
        consulta_inicial: 0,
        reeducacion_psicopedagogica_individual: 45,
        estimulacion: 45,
        psicologia_infantojuvenil_individual: 50,
        intervencion_psicoterapia_mes: 200,
        intervencion_estimulacion_mes: 180,
        terapia_familiar_individual: 60,
        psicologia_perinatal_individual: 50,
        asesoramiento_crianza_individual: 45,
        atencion_temprana_individual: 45,
        entrevista_inicial: 30,
        evaluacion_psicologica: 350,
        psicoterapia: 50,
        reeducacion_psicopedagogica: 45,
      },

      logopedia: {
        consulta_inicial: 0,
        intervencion_logopedia_mes: 140,
        intervencion_deglucion_mes: 120,
        sesion_logopedia_individual: 35,
        entrevista_inicial: 30,
        evaluacion_logopedia: 150,
        deglucion: 25,
        intervencion_logopedia_neae_mes: 140,
        logopedia: 35,
      },

      nutricion: {
        consulta_nutricional: 50,
      },

      administracion: {
        clase_administracion: 15,
      },
    }),
    []
  );

  const PRECIOS_NUEVOS_POR_ESPECIALIDAD = useMemo(
    () => ({
      psicologia: {
        entrevista_inicial: 60,
        evaluacion_psicologica: 55,
        devolucion_informe_psicologia: 55,
        sesion_psicologia: 50,
        sesion_estimulacion: 45,
        no_disponible: 0,

        reeducacion_pedagogica_neae_mes: 180,
        reeducacion_pedagogica_neae_sesion: 45,
        altas_capacidades_neae_mes: 180,
        altas_capacidades_neae_sesion: 45,
      },

      logopedia: {
        entrevista_inicial: 50,
        evaluacion_logopedica: 45,
        devolucion_informe_logopedia: 45,
        sesion_logopedia: 40,
        no_disponible: 0,

        intervencion_logopedia_mes: 160,
        reeducacion_lenguaje_neae_mes: 160,
        reeducacion_lenguaje_neae_sesion: 40,
      },

      /*
       * En AgendarSesion, Nutrición no tiene todavía price en SERVICES_BY_SPEC.
       * Si la sesión trae precio/importe/tarifa desde la API, se usará ese valor.
       */
      nutricion: {
        no_disponible: 0,
      },

      administracion: {
        no_disponible: 0,
      },
    }),
    []
  );

  const normalizePrecio = (v) =>
    String(v ?? "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[\s-]+/g, "_");

  const toNum = (v) => {
    if (v === null || v === undefined || v === "") return null;

    const n = Number(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : null;
  };

  const normalizeServiceCode = (value) => {
    const key = normalizePrecio(value);

    const aliases = {
      // Psicología
      psicoterapia: "sesion_psicologia",
      psicologia_infantojuvenil_individual: "sesion_psicologia",
      estimulacion: "sesion_estimulacion",
      atencion_temprana_individual: "sesion_estimulacion",

      // Logopedia
      logopedia: "sesion_logopedia",
      sesion_logopedia_individual: "sesion_logopedia",
      evaluacion_logopedia: "evaluacion_logopedica",
    };

    return aliases[key] || key;
  };

  const getSessionDateString = (s) => {
    const raw =
      s?.start_datetime ||
      s?.date ||
      s?.created_at ||
      "";

    if (!raw) return "";

    const value = String(raw).trim();

    // Si viene en formato MySQL YYYY-MM-DD HH:mm:ss
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const getPriceTableForSession = (s) => {
    const sessionDate = getSessionDateString(s);

    /*
     * Si por algún motivo una sesión antigua no trae fecha,
     * usamos tarifas nuevas como fallback conservador.
     */
    if (!sessionDate) {
      return PRECIOS_NUEVOS_POR_ESPECIALIDAD;
    }

    return sessionDate >= FECHA_CAMBIO_TARIFAS
      ? PRECIOS_NUEVOS_POR_ESPECIALIDAD
      : PRECIOS_ANTIGUOS_POR_ESPECIALIDAD;
  };

  const getPrecioSesion = (s) => {
    if (!s) return 0;

    /*
     * 1. El precio guardado en la propia sesión manda siempre.
     *    Esto protegerá el histórico en cuanto empecemos a guardar
     *    sessions.precio al crear nuevas sesiones.
     */
    const direct =
      s?.precio ??
      s?.price ??
      s?.importe ??
      s?.amount ??
      s?.tarifa;

    const directNumber = toNum(direct);

    if (directNumber !== null && directNumber >= 0) {
      return directNumber;
    }

    /*
     * 2. Si no hay precio guardado, usamos la tarifa histórica
     *    correspondiente a la fecha de la sesión.
     */
    const specialty = getBaseSpecialtyCode(getSpecialtyCode(s));
    const serviceCodeOriginal = normalizePrecio(
      s?.service_code ||
      s?.servicio ||
      s?.service
    );

    const priceTable = getPriceTableForSession(s);
    const specialtyPrices = priceTable[specialty] || {};

    /*
     * En las tarifas antiguas intentamos primero el código exacto
     * para respetar nombres históricos como intervencion_psicoterapia_mes.
     */
    if (
      serviceCodeOriginal &&
      Object.prototype.hasOwnProperty.call(
        specialtyPrices,
        serviceCodeOriginal
      )
    ) {
      return Number(specialtyPrices[serviceCodeOriginal]) || 0;
    }

    /*
     * Si no existe el código exacto, probamos el alias normalizado.
     */
    const serviceCode = normalizeServiceCode(serviceCodeOriginal);

    if (
      serviceCode &&
      Object.prototype.hasOwnProperty.call(
        specialtyPrices,
        serviceCode
      )
    ) {
      return Number(specialtyPrices[serviceCode]) || 0;
    }

    return 0;
  };

  const ingresosPendientesCalculados = useMemo(() => {
    if (!Array.isArray(sesionesPendientes) || sesionesPendientes.length === 0) {
      return 0;
    }

    return sesionesPendientes.reduce((acc, s) => {
      const precio = getPrecioSesion(s);
      const factor = getFactorCentro(s);
      return acc + precio * factor;
    }, 0);
  }, [
    sesionesPendientes,
    PRECIOS_ANTIGUOS_POR_ESPECIALIDAD,
    PRECIOS_NUEVOS_POR_ESPECIALIDAD,
  ]);

  const prevision = useMemo(() => {
    const gastosNum = Number(totalGastos || 0);
    const ingresosCobrados = ingresosNum;
    const ingresosPend = Number(ingresosPendientesCalculados || 0);

    const ingresosProyectados = ingresosCobrados + ingresosPend;
    const balanceProyectado = ingresosProyectados - gastosNum;
    const faltanteParaEquilibrio = Math.max(0, gastosNum - ingresosProyectados);

    return {
      ingresosCobrados,
      ingresosPendientes: ingresosPend,
      ingresosProyectados,
      gastosProyectados: gastosNum,
      balanceProyectado,
      faltanteParaEquilibrio,
      sesionesPendientesCount: Array.isArray(sesionesPendientes)
        ? sesionesPendientes.length
        : 0,
    };
  }, [
    ingresosNum,
    ingresosPendientesCalculados,
    totalGastos,
    sesionesPendientes,
  ]);

  useEffect(() => {
    if (subTab !== "comparativa") return;

    let alive = true;

    (async () => {
      try {
        setComparativaLoading(true);
        setComparativaError("");

        const items = await pagosComparativa(selectedYear);

        if (!alive) return;
        setComparativaData(items || []);
      } catch (e) {
        if (!alive) return;
        setComparativaError(e?.message || "Error al cargar comparativa");
        setComparativaData([]);
      } finally {
        if (alive) setComparativaLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [subTab, selectedYear]);

  return (
    <div className="balance-tab">
      <div className="balance-header">
        <h3>📊 Balance del mes {mesTexto || mes}</h3>

        <div className="balance-mes-filter">
          <label>
            Mes:
            <input
              type="month"
              value={mes}
              onChange={(e) => setMes(e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="balance-subtabs">
        <button
          type="button"
          className={subTab === "resumen" ? "active" : ""}
          onClick={() => setSubTab("resumen")}
        >
          Resumen
        </button>

        <button
          type="button"
          className={subTab === "prevision" ? "active" : ""}
          onClick={() => setSubTab("prevision")}
        >
          Previsión
        </button>

        <button
          type="button"
          className={subTab === "comparativa" ? "active" : ""}
          onClick={() => setSubTab("comparativa")}
        >
          Comparativa
        </button>
      </div>

      {subTab === "resumen" && (
        <>
          <div className="balance-resumen">
            <div className="balance-card ingresos">
              <span>Ingresos reales del centro</span>
              <strong>{ingresosNum.toFixed(2)} €</strong>
            </div>

            <div className="balance-card gastos">
              <span>Gastos del mes</span>
              <strong>{Number(totalGastos || 0).toFixed(2)} €</strong>
            </div>

            <div
              className={`balance-card balance-neto ${
                balance >= 0 ? "positivo" : "negativo"
              }`}
            >
              <span>Balance</span>
              <strong>{balance.toFixed(2)} €</strong>
            </div>
          </div>

          <div className="balance-add">
            <h4>Gastos del mes</h4>

            <div className="balance-add-row">
              <input
                type="text"
                placeholder="Concepto (ej. Alquiler, Luz...)"
                value={nuevoConcepto}
                onChange={(e) => setNuevoConcepto(e.target.value)}
              />

              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Importe €"
                value={nuevoImporte}
                onChange={(e) => setNuevoImporte(e.target.value)}
              />

              <button
                type="button"
                className="btn-add"
                onClick={handleAddGasto}
                title="Añadir gasto"
              >
                ➕
              </button>
            </div>
          </div>

          {loading ? (
            <p>Cargando gastos...</p>
          ) : error ? (
            <p className="balance-error">{error}</p>
          ) : gastos.length === 0 ? (
            <p>No hay gastos registrados para este mes.</p>
          ) : (
            <div className="pv-table-wrapper" style={{ marginTop: "0.75rem" }}>
              <table className="pv-table">
                <thead>
                  <tr>
                    <th>Concepto</th>
                    <th>Importe (€)</th>
                    <th>Fecha registro</th>
                  </tr>
                </thead>

                <tbody>
                  {gastos.map((g) => (
                    <tr key={g.id}>
                      <td>{g.concepto}</td>
                      <td>{Number(g.importe).toFixed(2)} €</td>
                      <td>
                        {g.created_at
                          ? new Date(g.created_at).toLocaleDateString("es-ES")
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {subTab === "prevision" && (
        <div className="balance-prevision">
          <h4>Previsión real del mes</h4>

          <div className="balance-resumen">
            <div className="balance-card ingresos">
              <span>Ingresos reales previstos</span>
              <strong>{prevision.ingresosProyectados.toFixed(2)} €</strong>
            </div>

            <div className="balance-card gastos">
              <span>Gastos previstos</span>
              <strong>{prevision.gastosProyectados.toFixed(2)} €</strong>
            </div>

            <div
              className={`balance-card balance-neto ${
                prevision.balanceProyectado >= 0 ? "positivo" : "negativo"
              }`}
            >
              <span>Balance previsto</span>
              <strong>{prevision.balanceProyectado.toFixed(2)} €</strong>
            </div>
          </div>

          <p className="balance-prevision-text">
            De los ingresos reales previstos para este mes (
            <strong>{prevision.ingresosProyectados.toFixed(2)} €</strong>), ya
            tienes cobrados{" "}
            <strong>{prevision.ingresosCobrados.toFixed(2)} €</strong> y quedan
            pendientes de cobro aproximadamente{" "}
            <strong>{prevision.ingresosPendientes.toFixed(2)} €</strong>.
          </p>

          <p className="balance-prevision-text">
            Sesiones pendientes contadas:{" "}
            <strong>{prevision.sesionesPendientesCount}</strong>
          </p>

          {prevision.faltanteParaEquilibrio > 0 ? (
            <p className="balance-prevision-text">
              Para cubrir todos los gastos previstos te faltarían unos{" "}
              <strong>{prevision.faltanteParaEquilibrio.toFixed(2)} €</strong>.
            </p>
          ) : (
            <p className="balance-prevision-text">
              Con los ingresos reales previstos, cubrirías todos los gastos del
              mes y cerrarías con balance positivo.
            </p>
          )}
        </div>
      )}

      {subTab === "comparativa" && (
        <div style={{ marginTop: 12 }}>
          {comparativaLoading ? <p>Cargando comparativa…</p> : null}

          {comparativaError ? (
            <p className="balance-error">{comparativaError}</p>
          ) : null}

          {!comparativaLoading && !comparativaError ? (
            <BalanceComparativa
              data={comparativaData}
              principalYear={selectedYear}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}